import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  TemplateRef,
  signal,
  computed,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayModule,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface DropdownCheckOption {
  label: string;
  value: string | null;
  disabled?: boolean;
}

export interface ChecklistSelection {
  selectedValues: string[];
  displayText: string;
  isDefault: boolean;
}

@Component({
  selector: 'app-dropdown-checklist-v2',
  templateUrl: './dropdown-checkbox-v2.component.html',
  styleUrl: './dropdown-checkbox-v2.component.scss',
  standalone: true,
  imports: [CommonModule, OverlayModule, FormsModule],
})
export class DropdownChecklistV2Component implements OnDestroy, OnInit, OnChanges {
  @Input() options: DropdownCheckOption[] = [];
  @Input() label = '';
  @Input() subLabel = '';
  @Input() placeholder = '== Select an option ==';
  @Input() showDefaultOption = true;
  @Input() defaultOptionLabel = 'Default - Show All';
  @Input() selectedValues: string[] = [];
  @Input() labelWidth = '130px';
  @Input() openArrowClass = 'orange-text';
  @Input() closeArrowClass = 'violet-text';
  @Input() hasError = false;
  @Input() showError = false;
  @Input() disabled = false;
  @Input() errorMessage = '';

  @Output() selectionChange = new EventEmitter<ChecklistSelection>();

  @ViewChild('dropdownPanel') panelTemplate!: TemplateRef<any>;
  @ViewChild('cardTrigger', { read: ElementRef }) cardTriggerRef!: ElementRef;

  isOpen = signal(false);
  displayText = '';
  defaultChecked = false;
  selectedOptions: Map<string, boolean> = new Map();

  arrowIcon = computed(() => (this.isOpen() ? 'arrow_drop_up' : 'arrow_drop_down'));
  arrowClass = computed(() => (this.isOpen() ? this.openArrowClass : this.closeArrowClass));

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) {}

  ngOnInit(): void {
    this.initializeSelections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedValues'] || changes['options']) {
      this.initializeSelections();
    }
  }

  ngOnDestroy(): void {
    this.closePanel();
    this.overlayRef?.dispose();
  }

  // ─── Initialization ───────────────────────────────────────────────────────

  private initializeSelections(): void {
    const selectedValuesArray = Array.isArray(this.selectedValues)
      ? this.selectedValues.map(val => String(val))
      : (this.selectedValues ? [String(this.selectedValues)] : []);

    const allOptionValues = this.getValidOptions().map(opt => opt.value!);
    const allSelected = allOptionValues.length > 0 &&
      allOptionValues.every(val => selectedValuesArray.includes(String(val)));

    // Only enter "default / all-selected" state when showDefaultOption is true
    // AND either nothing is explicitly selected OR everything is selected.
    if (this.showDefaultOption && (selectedValuesArray.length === 0 || allSelected)) {
      this.defaultChecked = true;
      this.options.forEach(option => {
        if (option.value !== null) {
          this.selectedOptions.set(option.value, true);
        }
      });
      this.displayText = this.defaultOptionLabel;
    } else {
      // When showDefaultOption is false, empty selectedValues simply means nothing
      // is pre-checked — do NOT treat it as "all selected".
      this.defaultChecked = false;
      this.options.forEach(option => {
        if (option.value !== null) {
          const isSelected = selectedValuesArray.some(v => String(v) === String(option.value));
          this.selectedOptions.set(option.value, isSelected);
        }
      });
      this.updateDisplayText();
    }
  }

  // ─── Panel open/close ─────────────────────────────────────────────────────

  toggleDropdown(): void {
    if (this.disabled) { return; } 
    if (this.isOpen()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel(): void {
    if (this.overlayRef?.hasAttached()) { return; }

    const cardEl = this.cardTriggerRef.nativeElement as HTMLElement;

    const positions: ConnectedPosition[] = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom' },
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(cardEl)
      .withPositions(positions)
      .withPush(false)
      .withDefaultOffsetY(0);

    const scrollStrategy = this.overlay.scrollStrategies.reposition();
    const cardRect = cardEl.getBoundingClientRect();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      minWidth: cardRect.width,
      maxWidth: cardRect.width,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.backdropClick().subscribe(() => this.closePanel());
    this.overlayRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') { this.closePanel(); }
    });

    const portal = new TemplatePortal(this.panelTemplate, this.vcr);
    this.overlayRef.attach(portal);
    this.isOpen.set(true);
  }

  closePanel(): void {
    this.overlayRef?.detach();
    this.isOpen.set(false);
  }

  // ─── Checkbox logic ───────────────────────────────────────────────────────

  isOptionChecked(value: string): boolean {
    return this.selectedOptions.get(value) || false;
  }

  onDefaultChange(): void {
    if (this.defaultChecked) {
      this.selectedOptions.forEach((_, key) => this.selectedOptions.set(key, true));
      this.displayText = this.defaultOptionLabel;
    } else {
      this.selectedOptions.forEach((_, key) => this.selectedOptions.set(key, false));
      this.displayText = '';
    }
    this.emitSelection();
  }

  onOptionChange(value: string): void {
    const option = this.options.find(opt => opt.value === value);
    if (option?.disabled) { return; }

    const currentState = this.selectedOptions.get(value) || false;
    const newState = !currentState;

    if (this.defaultChecked && !newState) {
      this.defaultChecked = false;
      this.selectedOptions.forEach((_, key) => {
        const opt = this.options.find(o => o.value === key);
        this.selectedOptions.set(key, opt?.disabled ? false : (key === value ? false : true));
      });
      this.updateDisplayText();
      this.emitSelection();
      return;
    }

    this.selectedOptions.set(value, newState);
    this.defaultChecked = this.showDefaultOption && this.areAllOptionsSelected();
    this.updateDisplayText();
    this.emitSelection();
  }

  private areAllOptionsSelected(): boolean {
    return this.getValidOptions().every(opt => this.selectedOptions.get(opt.value!) === true);
  }

  private updateDisplayText(): void {
    if (this.defaultChecked) {
      this.displayText = this.defaultOptionLabel;
      return;
    }

    const selected = this.getSelectedValues();

    if (selected.length === 0) {
      this.displayText = '';
      return;
    }

    this.displayText = selected
      .map(val => this.options.find(opt => opt.value === val)?.label ?? '')
      .filter(Boolean)
      .join(', ');
  }

  private getSelectedValues(): string[] {
    const selected: string[] = [];
    this.selectedOptions.forEach((isChecked, value) => {
      if (isChecked) { selected.push(value); }
    });
    return selected;
  }

  private emitSelection(): void {
    const selectedValues = this.defaultChecked ? [] : this.getSelectedValues();
    this.selectionChange.emit({
      selectedValues,
      displayText: this.displayText,
      isDefault: this.defaultChecked,
    });
  }

  getValidOptions(): DropdownCheckOption[] {
    return this.options.filter(opt => opt.value !== null);
  }
}
